import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  CircularProgress,
  Alert,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Divider,
  Stack,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Send as SubmitIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  getExpenses,
  getExpenseCategories,
  getExpenseVendors,
  getExpenseTypes,
  getExpenseStats,
  deleteExpense,
  submitExpense,
  approveExpense,
  rejectExpense
} from '../redux/actions/expenseActions';

// Tab Panel Component
const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`expense-tabpanel-${index}`}
      aria-labelledby={`expense-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const Expenses = () => {
  const dispatch = useDispatch();
  const { expenses, stats, loading, error, pagination } = useSelector(state => state.expenses);
  const { categories } = useSelector(state => state.expenseCategories);
  const { vendors } = useSelector(state => state.expenseVendors);
  const { expenseTypes } = useSelector(state => state.expenseTypes);

  // State management
  const [tabValue, setTabValue] = useState(0);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    search: '',
    category_id: '',
    expense_type_id: '',
    vendor_id: '',
    status: '',
    start_date: null,
    end_date: null,
    sort_by: 'created_at',
    sort_order: 'DESC'
  });
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [actionDialog, setActionDialog] = useState({
    open: false,
    type: '', // 'view', 'edit', 'delete', 'submit', 'approve', 'reject'
    title: '',
    content: null
  });
  const [expenseForm, setExpenseForm] = useState({
    open: false,
    mode: 'create', // 'create' or 'edit'
    data: {
      title: '',
      description: '',
      expense_date: new Date(),
      category_id: '',
      expense_type_id: '',
      vendor_id: '',
      amount: 0,
      tax_amount: 0,
      total_amount: 0,
      currency: 'INR',
      status: 'draft',
      priority: 'medium',
      due_date: null,
      reference_number: '',
      bill_number: '',
      bill_date: null,
      is_reimbursable: false,
      notes: ''
    }
  });

  // Load data on component mount
  useEffect(() => {
    dispatch(getExpenseCategories({ is_active: true }));
    dispatch(getExpenseVendors({ is_active: true }));
    dispatch(getExpenseTypes({ is_active: true }));
    loadExpenses();
    loadStats();
  }, []);

  // Load expenses when filters change
  useEffect(() => {
    loadExpenses();
  }, [filters.page, filters.limit, filters.category_id, filters.expense_type_id, filters.vendor_id, filters.status, filters.start_date, filters.end_date, filters.sort_by, filters.sort_order]);

  const loadExpenses = () => {
    const filterData = {
      ...filters,
      start_date: filters.start_date ? filters.start_date.toISOString().split('T')[0] : null,
      end_date: filters.end_date ? filters.end_date.toISOString().split('T')[0] : null
    };
    dispatch(getExpenses(filterData));
  };

  const loadStats = () => {
    const statsFilters = {
      start_date: filters.start_date ? filters.start_date.toISOString().split('T')[0] : null,
      end_date: filters.end_date ? filters.end_date.toISOString().split('T')[0] : null,
      category_id: filters.category_id || null
    };
    dispatch(getExpenseStats(statsFilters));
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handlePageChange = (event, page) => {
    setFilters(prev => ({ ...prev, page: page + 1 }));
  };

  const handleRowsPerPageChange = (event) => {
    setFilters(prev => ({
      ...prev,
      limit: parseInt(event.target.value, 10),
      page: 1
    }));
  };

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, page: 1 }));
    loadExpenses();
  };

  const handleClearFilters = () => {
    setFilters({
      page: 1,
      limit: 10,
      search: '',
      category_id: '',
      expense_type_id: '',
      vendor_id: '',
      status: '',
      start_date: null,
      end_date: null,
      sort_by: 'created_at',
      sort_order: 'DESC'
    });
  };

  const handleActionClick = (expense, action) => {
    setSelectedExpense(expense);
    setActionDialog({
      open: true,
      type: action,
      title: getActionTitle(action, expense),
      content: getActionContent(action, expense)
    });
  };

  const getActionTitle = (action, expense) => {
    const titles = {
      view: `View Expense - ${expense.expense_number}`,
      edit: `Edit Expense - ${expense.expense_number}`,
      delete: `Delete Expense - ${expense.expense_number}`,
      submit: `Submit Expense - ${expense.expense_number}`,
      approve: `Approve Expense - ${expense.expense_number}`,
      reject: `Reject Expense - ${expense.expense_number}`
    };
    return titles[action] || 'Action';
  };

  const getActionContent = (action, expense) => {
    switch (action) {
      case 'view':
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Expense Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Expense Number:</Typography>
                <Typography variant="body1">{expense.expense_number}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Title:</Typography>
                <Typography variant="body1">{expense.title}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Amount:</Typography>
                <Typography variant="body1">₹{expense.total_amount?.toLocaleString()}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Status:</Typography>
                <Chip label={expense.status} color={getStatusColor(expense.status)} size="small" />
              </Grid>
            </Grid>
          </Box>
        );
      case 'delete':
        return (
          <Typography>
            Are you sure you want to delete this expense? This action cannot be undone.
          </Typography>
        );
      case 'submit':
        return (
          <Box>
            <Typography variant="body1" gutterBottom>
              Submit expense "{expense.title}" for approval?
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes (Optional)"
              placeholder="Add any notes for the approver..."
              margin="normal"
            />
          </Box>
        );
      case 'approve':
        return (
          <Box>
            <Typography variant="body1" gutterBottom>
              Approve expense "{expense.title}"?
            </Typography>
            <TextField
              fullWidth
              type="number"
              label="Approved Amount"
              defaultValue={expense.total_amount}
              margin="normal"
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Comments (Optional)"
              placeholder="Add any comments..."
              margin="normal"
            />
          </Box>
        );
      case 'reject':
        return (
          <Box>
            <Typography variant="body1" gutterBottom>
              Reject expense "{expense.title}"?
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Reason for Rejection"
              placeholder="Please provide a reason for rejection..."
              margin="normal"
              required
            />
          </Box>
        );
      default:
        return null;
    }
  };

  const handleActionConfirm = () => {
    const { type } = actionDialog;
    
    switch (type) {
      case 'delete':
        dispatch(deleteExpense(selectedExpense.id));
        break;
      case 'submit':
        dispatch(submitExpense(selectedExpense.id, ''));
        break;
      case 'approve':
        dispatch(approveExpense(selectedExpense.id, selectedExpense.total_amount, ''));
        break;
      case 'reject':
        dispatch(rejectExpense(selectedExpense.id, ''));
        break;
      default:
        break;
    }
    
    setActionDialog({ open: false, type: '', title: '', content: null });
    setSelectedExpense(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      submitted: 'warning',
      approved: 'success',
      rejected: 'error',
      paid: 'info',
      reimbursed: 'primary',
      cancelled: 'error'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'success',
      medium: 'warning',
      high: 'error',
      urgent: 'error'
    };
    return colors[priority] || 'default';
  };

  // Summary Cards Component
  const SummaryCards = () => (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <MoneyIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
              <Box>
                <Typography variant="h6">
                  ₹{stats?.totalAmount?.toLocaleString() || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Amount
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AssessmentIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
              <Box>
                <Typography variant="h6">
                  {stats?.totalExpenses || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Expenses
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TrendingUpIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
              <Box>
                <Typography variant="h6">
                  {expenses?.filter(e => e.status === 'approved').length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Approved
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <BusinessIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
              <Box>
                <Typography variant="h6">
                  {expenses?.filter(e => e.status === 'pending').length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  // Filters Component
  const FiltersSection = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleSearch} edge="end">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <Autocomplete
              size="small"
              options={categories || []}
              getOptionLabel={(option) => option.name}
              value={categories?.find(c => c.id === filters.category_id) || null}
              onChange={(event, newValue) => {
                handleFilterChange('category_id', newValue?.id || '');
              }}
              renderInput={(params) => (
                <TextField {...params} label="Category" />
              )}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="submitted">Submitted</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="reimbursed">Reimbursed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={filters.sort_by}
                onChange={(e) => handleFilterChange('sort_by', e.target.value)}
                label="Sort By"
              >
                <MenuItem value="created_at">Created Date</MenuItem>
                <MenuItem value="expense_date">Expense Date</MenuItem>
                <MenuItem value="total_amount">Amount</MenuItem>
                <MenuItem value="title">Title</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Order</InputLabel>
              <Select
                value={filters.sort_order}
                onChange={(e) => handleFilterChange('sort_order', e.target.value)}
                label="Order"
              >
                <MenuItem value="ASC">Ascending</MenuItem>
                <MenuItem value="DESC">Descending</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={1}>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              startIcon={<FilterIcon />}
              size="small"
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  // Categories Management Component
  const CategoriesManagement = () => {
    const [categoryForm, setCategoryForm] = useState({
      open: false,
      mode: 'create', // 'create' or 'edit'
      data: {
        name: '',
        code: '',
        description: '',
        parent_id: null,
        is_active: true,
        sort_order: 0,
        color: '#2196F3',
        icon: 'Receipt'
      }
    });
    const [categorySearch, setCategorySearch] = useState('');

    const handleCategoryFormChange = (field, value) => {
      setCategoryForm(prev => ({
        ...prev,
        data: { ...prev.data, [field]: value }
      }));
    };

    const handleCategorySubmit = () => {
      // TODO: Implement category creation/update
      console.log('Category form data:', categoryForm.data);
      setCategoryForm(prev => ({ ...prev, open: false }));
    };

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6">Categories Management</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage expense categories and subcategories
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCategoryForm(prev => ({ ...prev, open: true, mode: 'create', data: { name: '', code: '', description: '', parent_id: null, is_active: true, sort_order: 0, color: '#2196F3', icon: 'Receipt' } }))}
          >
            Add Category
          </Button>
        </Box>

        {/* Search */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <TextField
              fullWidth
              size="small"
              label="Search Categories"
              value={categorySearch}
              onChange={(e) => setCategorySearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </CardContent>
        </Card>

        {/* Categories List */}
        <Card>
          <CardContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Parent</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Sort Order</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories?.filter(cat => 
                    cat.name.toLowerCase().includes(categorySearch.toLowerCase()) ||
                    cat.code.toLowerCase().includes(categorySearch.toLowerCase())
                  ).map((category) => (
                    <TableRow key={category.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              backgroundColor: category.color || '#2196F3',
                              borderRadius: 1,
                              mr: 2
                            }}
                          />
                          <Typography variant="body2" fontWeight="medium">
                            {category.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {category.code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {category.description || 'No description'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {category.parent?.name || 'Root'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={category.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={category.is_active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {category.sort_order}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => setCategoryForm(prev => ({ 
                                ...prev, 
                                open: true, 
                                mode: 'edit', 
                                data: { ...category } 
                              }))}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                // TODO: Implement category deletion
                                console.log('Delete category:', category.id);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Category Form Dialog */}
        <Dialog
          open={categoryForm.open}
          onClose={() => setCategoryForm(prev => ({ ...prev, open: false }))}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {categoryForm.mode === 'create' ? 'Add New Category' : 'Edit Category'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Category Name"
                    value={categoryForm.data.name}
                    onChange={(e) => handleCategoryFormChange('name', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Category Code"
                    value={categoryForm.data.code}
                    onChange={(e) => handleCategoryFormChange('code', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Description"
                    value={categoryForm.data.description}
                    onChange={(e) => handleCategoryFormChange('description', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={categories?.filter(cat => cat.id !== categoryForm.data.id) || []}
                    getOptionLabel={(option) => option.name}
                    value={categories?.find(cat => cat.id === categoryForm.data.parent_id) || null}
                    onChange={(event, newValue) => {
                      handleCategoryFormChange('parent_id', newValue?.id || null);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Parent Category" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Sort Order"
                    value={categoryForm.data.sort_order}
                    onChange={(e) => handleCategoryFormChange('sort_order', parseInt(e.target.value) || 0)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Color"
                    type="color"
                    value={categoryForm.data.color}
                    onChange={(e) => handleCategoryFormChange('color', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={categoryForm.data.is_active}
                      onChange={(e) => handleCategoryFormChange('is_active', e.target.value)}
                      label="Status"
                    >
                      <MenuItem value={true}>Active</MenuItem>
                      <MenuItem value={false}>Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCategoryForm(prev => ({ ...prev, open: false }))}>
              Cancel
            </Button>
            <Button onClick={handleCategorySubmit} variant="contained">
              {categoryForm.mode === 'create' ? 'Create' : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  // Vendors Management Component
  const VendorsManagement = () => {
    const [vendorForm, setVendorForm] = useState({
      open: false,
      mode: 'create', // 'create' or 'edit'
      data: {
        name: '',
        contact_person: '',
        phone: '',
        email: '',
        address: '',
        gstin: '',
        pan: '',
        bank_name: '',
        account_number: '',
        ifsc_code: '',
        is_active: true,
        vendor_type: 'supplier',
        payment_terms: '',
        credit_limit: 0
      }
    });
    const [vendorSearch, setVendorSearch] = useState('');

    const handleVendorFormChange = (field, value) => {
      setVendorForm(prev => ({
        ...prev,
        data: { ...prev.data, [field]: value }
      }));
    };

    const handleVendorSubmit = () => {
      // TODO: Implement vendor creation/update
      console.log('Vendor form data:', vendorForm.data);
      setVendorForm(prev => ({ ...prev, open: false }));
    };

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6">Vendors Management</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage expense vendors and suppliers
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setVendorForm(prev => ({ ...prev, open: true, mode: 'create', data: { name: '', contact_person: '', phone: '', email: '', address: '', gstin: '', pan: '', bank_name: '', account_number: '', ifsc_code: '', is_active: true, vendor_type: 'supplier', payment_terms: '', credit_limit: 0 } }))}
          >
            Add Vendor
          </Button>
        </Box>

        {/* Search */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <TextField
              fullWidth
              size="small"
              label="Search Vendors"
              value={vendorSearch}
              onChange={(e) => setVendorSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </CardContent>
        </Card>

        {/* Vendors List */}
        <Card>
          <CardContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Contact Person</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vendors?.filter(vendor => 
                    vendor.name.toLowerCase().includes(vendorSearch.toLowerCase()) ||
                    vendor.contact_person?.toLowerCase().includes(vendorSearch.toLowerCase())
                  ).map((vendor) => (
                    <TableRow key={vendor.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {vendor.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {vendor.contact_person || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {vendor.phone || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {vendor.email || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={vendor.vendor_type}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={vendor.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={vendor.is_active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => setVendorForm(prev => ({ 
                                ...prev, 
                                open: true, 
                                mode: 'edit', 
                                data: { ...vendor } 
                              }))}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                // TODO: Implement vendor deletion
                                console.log('Delete vendor:', vendor.id);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Vendor Form Dialog */}
        <Dialog
          open={vendorForm.open}
          onClose={() => setVendorForm(prev => ({ ...prev, open: false }))}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {vendorForm.mode === 'create' ? 'Add New Vendor' : 'Edit Vendor'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Vendor Name"
                    value={vendorForm.data.name}
                    onChange={(e) => handleVendorFormChange('name', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Contact Person"
                    value={vendorForm.data.contact_person}
                    onChange={(e) => handleVendorFormChange('contact_person', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={vendorForm.data.phone}
                    onChange={(e) => handleVendorFormChange('phone', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={vendorForm.data.email}
                    onChange={(e) => handleVendorFormChange('email', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Address"
                    value={vendorForm.data.address}
                    onChange={(e) => handleVendorFormChange('address', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="GSTIN"
                    value={vendorForm.data.gstin}
                    onChange={(e) => handleVendorFormChange('gstin', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="PAN"
                    value={vendorForm.data.pan}
                    onChange={(e) => handleVendorFormChange('pan', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Bank Name"
                    value={vendorForm.data.bank_name}
                    onChange={(e) => handleVendorFormChange('bank_name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Account Number"
                    value={vendorForm.data.account_number}
                    onChange={(e) => handleVendorFormChange('account_number', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="IFSC Code"
                    value={vendorForm.data.ifsc_code}
                    onChange={(e) => handleVendorFormChange('ifsc_code', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Vendor Type</InputLabel>
                    <Select
                      value={vendorForm.data.vendor_type}
                      onChange={(e) => handleVendorFormChange('vendor_type', e.target.value)}
                      label="Vendor Type"
                    >
                      <MenuItem value="supplier">Supplier</MenuItem>
                      <MenuItem value="service_provider">Service Provider</MenuItem>
                      <MenuItem value="contractor">Contractor</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Payment Terms"
                    value={vendorForm.data.payment_terms}
                    onChange={(e) => handleVendorFormChange('payment_terms', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Credit Limit"
                    value={vendorForm.data.credit_limit}
                    onChange={(e) => handleVendorFormChange('credit_limit', parseFloat(e.target.value) || 0)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={vendorForm.data.is_active}
                      onChange={(e) => handleVendorFormChange('is_active', e.target.value)}
                      label="Status"
                    >
                      <MenuItem value={true}>Active</MenuItem>
                      <MenuItem value={false}>Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVendorForm(prev => ({ ...prev, open: false }))}>
              Cancel
            </Button>
            <Button onClick={handleVendorSubmit} variant="contained">
              {vendorForm.mode === 'create' ? 'Create' : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  // Expense Form Component
  const ExpenseFormDialog = () => {
    const handleExpenseFormChange = (field, value) => {
      setExpenseForm(prev => {
        const newData = { ...prev.data, [field]: value };
        
        // Calculate total amount when amount or tax changes
        if (field === 'amount' || field === 'tax_amount') {
          const amount = field === 'amount' ? value : (prev.data?.amount || 0);
          const tax = field === 'tax_amount' ? value : (prev.data?.tax_amount || 0);
          newData.total_amount = parseFloat(amount || 0) + parseFloat(tax || 0);
        }
        
        return {
          ...prev,
          data: newData
        };
      });
    };

    const handleExpenseSubmit = () => {
      // TODO: Implement expense creation/update
      console.log('Expense form data:', expenseForm.data);
      setExpenseForm(prev => ({ ...prev, open: false }));
    };

    // Don't render if expenseForm.data is undefined
    if (!expenseForm.data) {
      return null;
    }

    return (
      <Dialog
        open={expenseForm.open}
        onClose={() => setExpenseForm(prev => ({ ...prev, open: false }))}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {expenseForm.mode === 'create' ? 'Add New Expense' : 'Edit Expense'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Expense Title"
                  value={expenseForm.data?.title || ''}
                  onChange={(e) => handleExpenseFormChange('title', e.target.value)}
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  value={expenseForm.data?.description || ''}
                  onChange={(e) => handleExpenseFormChange('description', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Expense Date"
                  value={expenseForm.data?.expense_date || new Date()}
                  onChange={(date) => handleExpenseFormChange('expense_date', date)}
                  renderInput={(params) => <TextField {...params} fullWidth required />}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Due Date (Optional)"
                  value={expenseForm.data?.due_date || null}
                  onChange={(date) => handleExpenseFormChange('due_date', date)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={categories || []}
                  getOptionLabel={(option) => option.name}
                  value={categories?.find(cat => cat.id === expenseForm.data?.category_id) || null}
                  onChange={(event, newValue) => {
                    handleExpenseFormChange('category_id', newValue?.id || '');
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Category" required />
                  )}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={expenseTypes || []}
                  getOptionLabel={(option) => option.name}
                  value={expenseTypes?.find(type => type.id === expenseForm.data?.expense_type_id) || null}
                  onChange={(event, newValue) => {
                    handleExpenseFormChange('expense_type_id', newValue?.id || '');
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Expense Type" required />
                  )}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={vendors || []}
                  getOptionLabel={(option) => option.name}
                  value={vendors?.find(vendor => vendor.id === expenseForm.data?.vendor_id) || null}
                  onChange={(event, newValue) => {
                    handleExpenseFormChange('vendor_id', newValue?.id || '');
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Vendor (Optional)" />
                  )}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={expenseForm.data?.priority || 'medium'}
                    onChange={(e) => handleExpenseFormChange('priority', e.target.value)}
                    label="Priority"
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Amount"
                  value={expenseForm.data?.amount || 0}
                  onChange={(e) => handleExpenseFormChange('amount', parseFloat(e.target.value) || 0)}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Tax Amount"
                  value={expenseForm.data?.tax_amount || 0}
                  onChange={(e) => handleExpenseFormChange('tax_amount', parseFloat(e.target.value) || 0)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Total Amount"
                  value={expenseForm.data?.total_amount || 0}
                  InputProps={{
                    readOnly: true,
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Reference Number"
                  value={expenseForm.data?.reference_number || ''}
                  onChange={(e) => handleExpenseFormChange('reference_number', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Bill Number"
                  value={expenseForm.data?.bill_number || ''}
                  onChange={(e) => handleExpenseFormChange('bill_number', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Bill Date"
                  value={expenseForm.data?.bill_date || null}
                  onChange={(date) => handleExpenseFormChange('bill_date', date)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Reimbursable</InputLabel>
                  <Select
                    value={expenseForm.data?.is_reimbursable || false}
                    onChange={(e) => handleExpenseFormChange('is_reimbursable', e.target.value)}
                    label="Reimbursable"
                  >
                    <MenuItem value={false}>No</MenuItem>
                    <MenuItem value={true}>Yes</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={expenseForm.data?.status || 'draft'}
                    onChange={(e) => handleExpenseFormChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="submitted">Submitted</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Notes"
                  value={expenseForm.data?.notes || ''}
                  onChange={(e) => handleExpenseFormChange('notes', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpenseForm(prev => ({ ...prev, open: false }))}>
            Cancel
          </Button>
          <Button onClick={handleExpenseSubmit} variant="contained">
            {expenseForm.mode === 'create' ? 'Create Expense' : 'Update Expense'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Types Management Component
  const TypesManagement = () => {
    const [typeForm, setTypeForm] = useState({
      open: false,
      mode: 'create', // 'create' or 'edit'
      data: {
        name: '',
        code: '',
        description: '',
        category_id: '',
        is_reimbursable: false,
        requires_approval: false,
        approval_limit: 0,
        is_active: true
      }
    });
    const [typeSearch, setTypeSearch] = useState('');

    const handleTypeFormChange = (field, value) => {
      setTypeForm(prev => ({
        ...prev,
        data: { ...prev.data, [field]: value }
      }));
    };

    const handleTypeSubmit = () => {
      // TODO: Implement type creation/update
      console.log('Type form data:', typeForm.data);
      setTypeForm(prev => ({ ...prev, open: false }));
    };

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6">Types Management</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage expense types and configurations
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setTypeForm(prev => ({ ...prev, open: true, mode: 'create', data: { name: '', code: '', description: '', category_id: '', is_reimbursable: false, requires_approval: false, approval_limit: 0, is_active: true } }))}
          >
            Add Type
          </Button>
        </Box>

        {/* Search */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <TextField
              fullWidth
              size="small"
              label="Search Types"
              value={typeSearch}
              onChange={(e) => setTypeSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </CardContent>
        </Card>

        {/* Types List */}
        <Card>
          <CardContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Reimbursable</TableCell>
                    <TableCell>Approval Required</TableCell>
                    <TableCell>Approval Limit</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expenseTypes?.filter(type => 
                    type.name.toLowerCase().includes(typeSearch.toLowerCase()) ||
                    type.code.toLowerCase().includes(typeSearch.toLowerCase())
                  ).map((type) => (
                    <TableRow key={type.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {type.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {type.code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={type.category?.name || 'N/A'}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={type.is_reimbursable ? 'Yes' : 'No'}
                          size="small"
                          color={type.is_reimbursable ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={type.requires_approval ? 'Yes' : 'No'}
                          size="small"
                          color={type.requires_approval ? 'warning' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          ₹{type.approval_limit?.toLocaleString() || 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={type.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={type.is_active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => setTypeForm(prev => ({ 
                                ...prev, 
                                open: true, 
                                mode: 'edit', 
                                data: { ...type } 
                              }))}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                // TODO: Implement type deletion
                                console.log('Delete type:', type.id);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Type Form Dialog */}
        <Dialog
          open={typeForm.open}
          onClose={() => setTypeForm(prev => ({ ...prev, open: false }))}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {typeForm.mode === 'create' ? 'Add New Type' : 'Edit Type'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Type Name"
                    value={typeForm.data.name}
                    onChange={(e) => handleTypeFormChange('name', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Type Code"
                    value={typeForm.data.code}
                    onChange={(e) => handleTypeFormChange('code', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Description"
                    value={typeForm.data.description}
                    onChange={(e) => handleTypeFormChange('description', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    options={categories || []}
                    getOptionLabel={(option) => option.name}
                    value={categories?.find(cat => cat.id === typeForm.data.category_id) || null}
                    onChange={(event, newValue) => {
                      handleTypeFormChange('category_id', newValue?.id || '');
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Category" required />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Reimbursable</InputLabel>
                    <Select
                      value={typeForm.data.is_reimbursable}
                      onChange={(e) => handleTypeFormChange('is_reimbursable', e.target.value)}
                      label="Reimbursable"
                    >
                      <MenuItem value={false}>No</MenuItem>
                      <MenuItem value={true}>Yes</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Requires Approval</InputLabel>
                    <Select
                      value={typeForm.data.requires_approval}
                      onChange={(e) => handleTypeFormChange('requires_approval', e.target.value)}
                      label="Requires Approval"
                    >
                      <MenuItem value={false}>No</MenuItem>
                      <MenuItem value={true}>Yes</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Approval Limit"
                    value={typeForm.data.approval_limit}
                    onChange={(e) => handleTypeFormChange('approval_limit', parseFloat(e.target.value) || 0)}
                    disabled={!typeForm.data.requires_approval}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={typeForm.data.is_active}
                      onChange={(e) => handleTypeFormChange('is_active', e.target.value)}
                      label="Status"
                    >
                      <MenuItem value={true}>Active</MenuItem>
                      <MenuItem value={false}>Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTypeForm(prev => ({ ...prev, open: false }))}>
              Cancel
            </Button>
            <Button onClick={handleTypeSubmit} variant="contained">
              {typeForm.mode === 'create' ? 'Create' : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  // Expenses Table Component
  const ExpensesTable = () => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Expenses</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setExpenseForm(prev => ({ 
              ...prev, 
              open: true, 
              mode: 'create',
              data: {
                title: '',
                description: '',
                expense_date: new Date(),
                category_id: '',
                expense_type_id: '',
                vendor_id: '',
                amount: 0,
                tax_amount: 0,
                total_amount: 0,
                currency: 'INR',
                status: 'draft',
                priority: 'medium',
                due_date: null,
                reference_number: '',
                bill_number: '',
                bill_date: null,
                is_reimbursable: false,
                notes: ''
              }
            }))}
          >
            Add Expense
          </Button>
        </Box>
        
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Expense #</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {expenses?.map((expense) => (
                <TableRow key={expense.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {expense.expense_number}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {expense.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={expense.category?.name || 'N/A'}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {expense.vendor?.name || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      ₹{expense.total_amount?.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(expense.expense_date).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={expense.status}
                      size="small"
                      color={getStatusColor(expense.status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={expense.priority}
                      size="small"
                      color={getPriorityColor(expense.priority)}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="View">
                        <IconButton
                          size="small"
                          onClick={() => handleActionClick(expense, 'view')}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleActionClick(expense, 'edit')}
                          disabled={expense.status === 'paid' || expense.status === 'cancelled'}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      
                      {expense.status === 'draft' && (
                        <Tooltip title="Submit">
                          <IconButton
                            size="small"
                            onClick={() => handleActionClick(expense, 'submit')}
                          >
                            <SubmitIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      {expense.status === 'submitted' && (
                        <>
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              onClick={() => handleActionClick(expense, 'approve')}
                            >
                              <ApproveIcon />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              onClick={() => handleActionClick(expense, 'reject')}
                            >
                              <RejectIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleActionClick(expense, 'delete')}
                          disabled={expense.status === 'paid' || expense.status === 'cancelled'}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        {pagination && (
          <TablePagination
            component="div"
            count={pagination.totalItems || 0}
            page={filters.page - 1}
            onPageChange={handlePageChange}
            rowsPerPage={filters.limit}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
            }
          />
        )}
        
        {expenses?.length === 0 && !loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No expenses found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your filters or create a new expense
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Expense Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and track business expenses
          </Typography>
        </Box>

        {/* Tabs */}
        <Card sx={{ mb: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Overview" />
              <Tab label="Expenses" />
              <Tab label="Categories" />
              <Tab label="Vendors" />
              <Tab label="Types" />
            </Tabs>
          </Box>
        </Card>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          <SummaryCards />
          <ExpensesTable />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <FiltersSection />
          <ExpensesTable />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <CategoriesManagement />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <VendorsManagement />
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <TypesManagement />
        </TabPanel>

        {/* Expense Form Dialog */}
        <ExpenseFormDialog />

        {/* Action Dialog */}
        <Dialog
          open={actionDialog.open}
          onClose={() => setActionDialog({ open: false, type: '', title: '', content: null })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{actionDialog.title}</DialogTitle>
          <DialogContent>
            {actionDialog.content}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setActionDialog({ open: false, type: '', title: '', content: null })}
            >
              Cancel
            </Button>
            {actionDialog.type !== 'view' && (
              <Button
                onClick={handleActionConfirm}
                variant="contained"
                color={actionDialog.type === 'delete' ? 'error' : 'primary'}
              >
                {actionDialog.type === 'delete' ? 'Delete' :
                 actionDialog.type === 'submit' ? 'Submit' :
                 actionDialog.type === 'approve' ? 'Approve' :
                 actionDialog.type === 'reject' ? 'Reject' : 'Confirm'}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default Expenses;
